import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';
import { NextPage, NextPageContext } from 'next';

import { useTranslations } from '@lang';
import { SpaDays } from '@features/spa/pages/SpaDays';

import * as journeyActions from '@store/yourJourney/actions';
import { yourJourneySelectors } from '@store/yourJourney/selectors';

import { Router } from 'shared-routing';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { getAllSpaPackagesPrefetch } from '@features/spa/hooks/useGetAllSpaPackages';
import { getAvailableSpaPackagesPrefetch } from '@features/spa/hooks/useGetAvailableSpaPackages';
import { getSpaCategoriesPrefetch } from '@features/spa/hooks/useGetSpaCategories';
import { getSpaSettingsPrefetch } from '@features/spa';
import { parseDateToString } from '@utils/helpers';

type SpaPackagesProps = {
  categoryId: number | null;
  language: string;
  propertyTitle: string;
  socialFallbackImage: {
    url: string;
  };
  url: string;
  property?: any;
  store?: any;
  router?: any;
};

const SpaDaysPage: NextPage<SpaPackagesProps> = ({
  categoryId,
  language,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.spaDays');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <SpaDays categoryId={categoryId} language={language} />
    </>
  );
};

SpaDaysPage.getInitialProps = async ({
  property,
  store,
  language,
  url,
  res,
  query,
}: NextPageContext & SpaPackagesProps) => {
  if (!property.isSpaIntegrated) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  const spaDate = yourJourneySelectors.getActualSpaDateValue(store.getState());
  const { fromDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());
  const spaDateToSet = spaDate || fromDate;
  const client = new QueryClient();
  const isServer = !!res;
  await Promise.all([
    ...(isServer
      ? [
          getAllSpaPackagesPrefetch(client, property.id, language),
          getAvailableSpaPackagesPrefetch(
            client,
            property.id,
            { category: null, date: parseDateToString(spaDateToSet) },
            language
          ),
          getSpaCategoriesPrefetch(client, property.id),
          getSpaSettingsPrefetch(client, property.id),
        ]
      : []),
    store.dispatch(journeyActions.changeJourneyParam('spaDate', spaDateToSet)),
  ]);

  const category = query.category ? Number.parseInt(query.category as string, 10) : null;

  return {
    url,
    language,
    categoryId: category,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  };
};

export default SpaDaysPage;
